import { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { Table, Tag, Space, Input, Select, Button, Card, Skeleton } from 'antd';
import { SearchOutlined, EyeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigation } from '@refinedev/core';

interface YolYolakayOffer {
  id: number;
  clientId: number;
  client?: { fullName: string; phone: string };
  pickupLat: number;
  pickupLng: number;
  pickupAddress?: string;
  destinationLat: number;
  destinationLng: number;
  destinationAddress?: string;
  offeredPrice: number;
  passengerCount: number;
  maleCount?: number;
  femaleCount?: number;
  status: string;
  showOnMap: boolean;
  comment?: string;
  expiresAt?: string;
  createdAt: string;
  responses?: number;
}

const statusLabels: Record<string, string> = {
  ACTIVE: 'Faol',
  MATCHED: 'Topilgan',
  EXPIRED: 'Muddati o\'tgan',
  CANCELLED: 'Bekor qilingan',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'green',
  MATCHED: 'blue',
  EXPIRED: 'default',
  CANCELLED: 'red',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
};

export const YolYolakayOfferList = () => {
  const { show } = useNavigation();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');

  const { tableProps, setFilters } = useTable<YolYolakayOffer>({
    resource: 'admin/yol-yolakay/offers',
    syncWithLocation: true,
    filters: {
      permanent: [
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ],
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
  });

  const handleSearch = () => {
    const filters: any[] = [];
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Mijoz',
      dataIndex: 'client',
      key: 'client',
      render: (client: YolYolakayOffer['client']) => (
        <div>
          <div>{client?.fullName || 'Ismi yo\'q'}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{client?.phone}</div>
        </div>
      ),
    },
    {
      title: 'Olish joyi',
      key: 'pickup',
      render: (_: unknown, record: YolYolakayOffer) => (
        <div style={{ fontSize: 12 }}>
          <EnvironmentOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          {record.pickupAddress || `${record.pickupLat.toFixed(4)}, ${record.pickupLng.toFixed(4)}`}
        </div>
      ),
    },
    {
      title: 'Manzil',
      key: 'destination',
      render: (_: unknown, record: YolYolakayOffer) => (
        <div style={{ fontSize: 12 }}>
          <EnvironmentOutlined style={{ color: '#f5222d', marginRight: 4 }} />
          {record.destinationAddress || `${record.destinationLat.toFixed(4)}, ${record.destinationLng.toFixed(4)}`}
        </div>
      ),
    },
    {
      title: 'Taklif narxi',
      dataIndex: 'offeredPrice',
      key: 'offeredPrice',
      render: (price: number) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(price)}</span>
      ),
    },
    {
      title: 'Yo\'lovchilar',
      key: 'passengers',
      render: (_: unknown, record: YolYolakayOffer) => (
        <div>
          <div>{record.passengerCount} nafar</div>
          {(record.maleCount != null || record.femaleCount != null) && (
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>
              {record.maleCount != null && `E: ${record.maleCount}`}
              {record.maleCount != null && record.femaleCount != null && ' / '}
              {record.femaleCount != null && `A: ${record.femaleCount}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Xaritada',
      dataIndex: 'showOnMap',
      key: 'showOnMap',
      render: (showOnMap: boolean) => (
        <Tag color={showOnMap ? 'green' : 'default'}>
          {showOnMap ? 'Ha' : 'Yo\'q'}
        </Tag>
      ),
    },
    {
      title: 'Javoblar',
      dataIndex: 'responses',
      key: 'responses',
      width: 90,
      render: (count: number | undefined) => count ?? 0,
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return (
          <div>
            <div>{day}.{month}.{year}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{hours}:{mins}</div>
          </div>
        );
      },
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: YolYolakayOffer) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/yol-yolakay-offers', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title="Savdolashish takliflari"
      headerButtons={
        <Space>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            onPressEnter={handleSearch}
          />
          <Select
            placeholder="Holat"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={Object.entries(statusLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </Space>
      }
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        loading={
          tableProps.loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
            : false
        }
      />
    </List>
  );
};
